<template>
  <div>
    <section class="hero is-fullheight">
      <div class="hero-body">
        <div class="container has-text-centered">
          <h3 class="title has-text-white">Enviar ARP assinada</h3>
          <div class="box app-justification-box">
            <form @submit.prevent="enviar">
              <p v-if="tituloAta" class="arp-enviar-intro">
                <strong>{{ tituloAta }}</strong>
              </p>

              <b-field
                class="arp-enviar-field"
                label="Data de assinatura"
                :type="erroAssinatura ? 'is-danger' : ''"
                :message="erroAssinatura"
              >
                <b-datepicker
                  v-model="dataAssinaturaDate"
                  editable
                  icon="calendar-today"
                  :max-date="hoje"
                  placeholder="dd/mm/aaaa"
                  :disabled="enviando"
                />
              </b-field>

              <b-field
                class="arp-enviar-field"
                :type="erroArquivo ? 'is-danger' : ''"
                :message="erroArquivo"
              >
                <b-upload
                  v-model="pickerFiles"
                  drag-drop
                  accept=".pdf,application/pdf"
                  class="arp-enviar-upload"
                  :disabled="enviando"
                  @input="onArquivos"
                >
                  <div class="arp-enviar-upload-content content has-text-centered">
                    <b-icon
                      class="arp-enviar-upload-icon"
                      icon="upload"
                      size="is-medium"
                      type="is-primary"
                    />
                    <p>Solte seu arquivo aqui ou clique para selecionar</p>
                    <p class="arp-enviar-upload-hint">PDF da ARP assinada</p>
                  </div>
                </b-upload>
              </b-field>

              <ul v-if="arquivos.length" class="arp-enviar-file-list">
                <li
                  v-for="(arquivo, index) in arquivos"
                  :key="`${arquivo.name}-${arquivo.size}-${index}`"
                >
                  <span class="arp-enviar-filename" :title="arquivo.name">{{ arquivo.name }}</span>
                  <button
                    type="button"
                    class="delete is-small"
                    aria-label="Remover arquivo"
                    :disabled="enviando"
                    @click.prevent="removerArquivo(index)"
                  />
                </li>
              </ul>

              <div class="buttons-container">
                <button
                  type="button"
                  class="button is-primary rm-1 tm-2"
                  :class="{ 'is-loading': enviando }"
                  :disabled="enviando"
                  @click.stop.prevent="enviar"
                >
                  Enviar
                </button>
                <button
                  type="button"
                  class="button tm-2"
                  :disabled="enviando"
                  @click.stop.prevent="fechar"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  addMonthsSafe,
  businessDaysBetween,
  dateToFormIsoString,
  getPncpDocumentUploadSizeErrorMessage,
  isWithinPncpDocumentUploadLimit,
  obterArquivoParaEnvio,
  parseIsoDateLocal,
  startOfToday,
} from '../helpers';

export default {
  name: 'ArpEnviarAssinadaModal',
  props: {
    ata: {
      type: Object,
      required: true,
    },
    enviando: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dataAssinaturaDate: null,
      arquivos: [],
      pickerFiles: [],
      touched: false,
    };
  },
  computed: {
    hoje() {
      return startOfToday();
    },
    tituloAta() {
      const n = this.ata?.numero;
      const a = this.ata?.ano;
      if (n == null || a == null) return '';
      const padded = /^\d+$/.test(String(n)) ? String(n).padStart(3, '0') : n;
      return `Ata Nº ${padded}/${a}`;
    },
    dataAssinaturaIso() {
      return dateToFormIsoString(this.dataAssinaturaDate);
    },
    erroAssinatura() {
      if (!this.touched) return '';
      if (!this.dataAssinaturaIso) return 'Campo obrigatório';
      const d = parseIsoDateLocal(this.dataAssinaturaIso);
      if (!d) return 'Data inválida';
      if (d.getTime() > this.hoje.getTime()) return 'A assinatura não pode ser futura';

      const ini = parseIsoDateLocal(this.ata?.dataVigenciaInicio);
      if (ini && ini.getTime() < d.getTime()) {
        return 'A vigência cadastrada inicia antes desta assinatura';
      }

      const fim = parseIsoDateLocal(this.ata?.dataVigenciaFim);
      if (fim) {
        const limite = addMonthsSafe(d, 12);
        if (fim.getTime() > limite.getTime()) {
          return 'Na inclusão, a vigência máxima é de 12 meses a partir da assinatura';
        }
      }

      return '';
    },
    erroArquivo() {
      if (!this.touched) return '';
      const arquivo = obterArquivoParaEnvio(this.arquivos);
      if (!arquivo) return 'Selecione o PDF assinado';
      if (!isWithinPncpDocumentUploadLimit(arquivo.size)) {
        return getPncpDocumentUploadSizeErrorMessage();
      }
      const nome = String(arquivo.name || '').toLowerCase();
      if (nome && !nome.endsWith('.pdf')) return 'Envie um arquivo PDF';
      return '';
    },
  },
  methods: {
    fechar() {
      this.$emit('close');
      if (typeof this.$parent.onCancel === 'function') {
        this.$parent.onCancel();
      }
      if (typeof this.$parent.close === 'function') {
        this.$parent.close();
      }
    },
    onArquivos(files) {
      this.pickerFiles = [];
      if (!files) return;

      const lista = Array.isArray(files) ? files : [files];
      const arquivo = lista[0];
      if (!arquivo) return;

      if (!isWithinPncpDocumentUploadLimit(arquivo.size)) {
        this.$alerta(getPncpDocumentUploadSizeErrorMessage(), 'is-danger');
        return;
      }

      const nome = String(arquivo.name || '').toLowerCase();
      if (nome && !nome.endsWith('.pdf')) {
        this.$alerta('Envie um arquivo PDF', 'is-danger');
        return;
      }

      this.arquivos = [arquivo];
    },
    removerArquivo(index) {
      if (this.enviando) return;
      const arquivos = this.arquivos.slice();
      arquivos.splice(index, 1);
      this.arquivos = arquivos;
    },
    enviar() {
      this.touched = true;
      if (this.erroAssinatura || this.erroArquivo) {
        this.$alerta(
          this.erroAssinatura || this.erroArquivo || 'Preencha os dados para enviar',
          'is-warning',
        );
        return;
      }

      const arquivo = obterArquivoParaEnvio(this.arquivos);
      if (!arquivo) return;

      const ass = parseIsoDateLocal(this.dataAssinaturaIso);
      if (ass) {
        const uteis = businessDaysBetween(ass, this.hoje);
        if (uteis > 10) {
          this.$alerta(
            `Atenção: a assinatura ocorreu há ${uteis} dias úteis. A lei prevê publicação em até 10 dias úteis (risco de auditoria).`,
            'is-warning',
          );
        }
      }

      this.$emit('enviar', {
        ataId: this.ata.id,
        dataAssinatura: this.dataAssinaturaIso,
        arquivo,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.buttons-container {
  margin-top: 1rem;
}

.arp-enviar-intro {
  color: #4a4a4a;
  line-height: 1.4;
  margin: 0 0 1rem;
  text-align: left;
}

.arp-enviar-field {
  margin-bottom: 1.5rem;
  text-align: left;
}

.arp-enviar-upload {
  display: block;
  width: 100%;
}

.arp-enviar-upload >>> .upload-draggable {
  width: 100%;
}

.arp-enviar-upload-content {
  align-items: center;
  background: #fafafa;
  border: 2px dashed #dbdbdb;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 140px;
  padding: 1rem;
  text-align: center;
}

.arp-enviar-upload-icon {
  margin-bottom: 0.5rem;
}

.arp-enviar-upload-hint {
  color: #8b8b8b;
  font-size: 0.85rem;
  margin-bottom: 0 !important;
  margin-top: 0.35rem !important;
}

.arp-enviar-file-list {
  list-style: none;
  margin: -0.75rem 0 1.25rem;
  padding: 0;
  text-align: left;
}

.arp-enviar-file-list li {
  align-items: center;
  border-bottom: 1px solid #ededed;
  display: flex;
  font-size: 0.875rem;
  gap: 0.5rem;
  padding: 0.35rem 0;
}

.arp-enviar-filename {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rm-1 {
  margin-right: 3px;
}

.app-justification-box {
  width: max-content;
  max-width: min(520px, 95vw);
  min-width: min(420px, 95vw);
  text-align: left;
}

@media (max-width: 768px) {
  .app-justification-box {
    min-width: 0;
    max-width: 90vw;
    width: 100%;
  }

  .tm-2 {
    margin-top: 10px;
  }
}
</style>
